import { QESSelect } from "@app/shared/qes-standards/forms/Select"
import { FC, useContext, useMemo, useState } from "react"
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfigurationPanel, PanelMode } from "@app/report-customization/build-reports/components/ColumnConfigurationPanel";
import { ExportModels } from "@app/report-customization/build-reports/interfaces/exportModels";
import { ExportColumn } from "@app/report-customization/build-reports/interfaces/exportColumns";
import { ReportFormatType } from "@app/exports/enumerations";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ColumnConfigurationPresetOption } from "../interfaces/columnConfigurationPresetOption";
import TooltipWrapper from "@app/shared/TooltipWrapper";

interface ColumnConfigurationSectionProps {
  hideHeading?: boolean;
  columnsEndpoint: (model: ExportModels) => Promise<ExportColumn[]>;
  exportModel: ExportModels;
  presets: ColumnConfigurationPresetOption[];
  selectedPreset: ColumnConfigurationPresetOption;
  reportType: ReportType;
  onPresetChange: (preset: ColumnConfigurationPresetOption) => void;
  onPresetsRefresh: () => Promise<void>;
  reportFormat?: ReportFormatType;
}

export const ColumnConfigurationSection: FC<ColumnConfigurationSectionProps> = (props: ColumnConfigurationSectionProps) => {
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const [hasConfigurationChanges, setHasConfigurationChanges] = useState(false);
  const presetsOptions = useMemo(() => {
    return props.presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>);
  }, [props.presets]);

  const allPresetNames = useMemo(() => {
    return props.presets.filter((p) => p.isPrivate).map((p) => p.name);
  }, [props.presets]);

  const isConfigurationDisabled = props.reportType === ReportType.AdequacyServicingProvider && (props.reportFormat === ReportFormatType.MA || props.reportFormat === ReportFormatType.MMP);

  const tooltipText = enabledFeatureFlags.has(Features.SHOW_MMP_HSD_FORMAT)
    ? "Column Configuration is not available for MA and MMP HSD Reports"
    : "Column Configuration is not available for MA HSD Reports";

  const handlePresetCreated = async (newPreset: ColumnConfigurationPresetOption) => {
    // Refresh the presets list to include the new preset
    await props.onPresetsRefresh();

    props.onPresetChange(newPreset);
  };

  return (
    <>
      {!props.hideHeading && (
        <div className="row mt-3">
          <h2 className="header-s col-auto">Column Configuration</h2>
        </div>
      )}
      <div className="row">
        <TooltipWrapper text={isConfigurationDisabled ? tooltipText : ""} containerClassName="col-4 p-0" triggerWidth="100%">
          <QESSelect
            label="Preset"
            containerClassName={isConfigurationDisabled ? "col-12" : "col-4"}
            value={props.selectedPreset?.id}
            onChange={(e) => {
              const selectedPreset = props.presets.find((p) => p?.id.toString() === e.target.value);
              if (selectedPreset) {
                props.onPresetChange(selectedPreset);
              }
            }}
            testId="report-customization__preset"
            disabled={isConfigurationDisabled}
          >
            {presetsOptions}
          </QESSelect>
        </TooltipWrapper>
        <div className="align-self-end col-auto px-0">
          {!!props.selectedPreset && props.selectedPreset.id !== -1 && (
            <FlyoutLink
              classNames="qes-button-secondary"
              testId="report-customization__create-new-preset"
              content={{
                body: (
                  <ColumnConfigurationPanel
                    setHasConfigurationChanges={setHasConfigurationChanges}
                    columnsEndpoint={props.columnsEndpoint}
                    exportModel={props.exportModel}
                    panelMode={PanelMode.View}
                    allPrivatePresetNames={allPresetNames}
                    reportType={props.reportType}
                    presetId={props.selectedPreset.id}
                  />
                ),
                title: "Column Configuration",
              }}
              size="40"
            >
              Preview
            </FlyoutLink>
          )}
          or
          <FlyoutLink
            classNames="qes-button-secondary"
            testId="report-customization__create-new-preset"
            content={{
              body: (
                <ColumnConfigurationPanel
                  setHasConfigurationChanges={setHasConfigurationChanges}
                  columnsEndpoint={props.columnsEndpoint}
                  exportModel={props.exportModel}
                  panelMode={PanelMode.Create}
                  allPrivatePresetNames={allPresetNames}
                  reportType={props.reportType}
                  onSuccessfulSubmit={handlePresetCreated}
                />
              ),
              title: "Column Configuration",
            }}
            modalContent={{
              body: "You will lose unsaved changes. Continue with closing the sidebar?",
              cancelButtonText: "Cancel",
              confirmButtonText: "Continue",
              title: "Warning",
            }}
            warnOnClose={hasConfigurationChanges}
            size="40"
            disabled={isConfigurationDisabled}
          >
            Create New
          </FlyoutLink>
        </div>
      </div>
    </>
  )
}